import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/core";
import type {
  CastMember,
  CrewSeasonDeactivateMode,
  EpisodeView,
  Id,
  ItemNumber,
  NoteColumnDef,
  PresenceUser,
  SceneCrewRole,
  ProgramText,
  SceneNumberingMode,
  ScriptColumn,
  ScriptDocument,
  StatusStepDef,
  ArchiveSceneMode,
  RestoreArchiveMode,
  DirectorScenePayload,
} from "@supermanus/document-model";
import {
  addComment,
  addDialogueCue,
  createId,
  formatEpisodeDisplayTitle,
  getCueSide,
  insertCueAfter,
  insertCueBefore,
  moveCueBlock,
  removeComment,
  removeCue,
  setAudioRowSpan,
  setCueCameraMeta,
  setCueCharacter,
  setCueNote,
  setCuePrintBorderBelow,
  setSceneNote,
  setSceneStatusSteps,
  setVisualRowSpan,
  toggleSceneStatusStep,
  updateComment,
  updateCueSide,
  updateSceneMeta,
  getCrewJobTitleOptions,
  getEpisodeScenes,
  getEpisodeProgramTexts,
  getEpisodeVaultKind,
  getVisibleNoteColumns,
  getNoteColumns,
  getSceneCues,
  listArchivedVersions,
  listEpisodeCrewNamesByJob,
  listEpisodes,
  listResolvedComments,
  mergeSeasonCrewIntoEpisodeRoles,
  episodePeerHasCrewPerson,
  getEpisodePeerCrewRoles,
  productionShowsSeasonChrome,
  reportNoteColumnContent,
  VAULT_RETENTION_DAYS,
} from "@supermanus/document-model";
import {
  DualColumnScript,
  type CellRef,
} from "./DualColumnScript";
import {
  RemoveNoteColumnDialog,
  type RemoveNoteColumnScope,
} from "./RemoveNoteColumnDialog";
import { InlineEditable } from "./InlineEditable";
import { EpisodeTitleLine } from "./EpisodeTitleLine";
import { DurationField } from "./DurationField";
import {
  buildSceneDeleteConfirmMessage,
  listSceneLinkedWarnings,
} from "./deleteWarnings";
import { ResolvedCommentsPanel } from "./ResolvedCommentsPanel";
import { ProductionDashboard } from "./ProductionDashboard";
import { ArchiveSceneDialog } from "./ArchiveSceneDialog";
import { SceneArchiveVersionsDialog } from "./SceneArchiveVersionsDialog";
import { EpisodeFolderTabs, episodeFolderLandscape, workspaceView } from "./EpisodeHome";
import { EpisodePageBar } from "./EpisodePageBar";
import { ViewMenu } from "./ViewMenu";
import { PaperFit } from "./PaperFit";
import { HelpPanel } from "./HelpPanel";
import { SettingsPanel } from "./SettingsPanel";
import type { HelpSectionId } from "./helpContent";
import {
  SceneHeaderColorMenu,
  sceneHeaderBg,
} from "./SceneHeaderColorMenu";
import { SceneHeadingLine } from "./SceneHeadingLine";
import { TimingView } from "./TimingView";
import { OutlineView } from "./OutlineView";
import { CrewView } from "./CrewView";
import { ProgramTextView } from "./ProgramTextView";
import { SourcesView } from "./SourcesView";
import { StatusView } from "./StatusView";
import {
  DEFAULT_BROWSER_TITLE,
  DocumentHead,
  formatBrowserDocumentTitle,
} from "./DocumentHead";
import { PrintPreview } from "./PrintPreview";
import { SceneMoveDialog } from "./SceneMoveDialog";
import { SceneNumberingNoticeDialog } from "./SceneNumberingNoticeDialog";
import { SceneTransferDialog } from "./SceneTransferDialog";
import { SceneMergeDialog } from "./SceneMergeDialog";
import { RestoreArchiveDialog } from "./RestoreArchiveDialog";
import type { DirectorArchiveRow } from "./directorApi";
import { useDirectorWorkspace } from "./useDirectorWorkspace";
import { UnlockDirectorDialog } from "./UnlockDirectorDialog";
import { useEditorSettings, clampEpisodeZoom } from "./editorSettings";
import { useRtfExport } from "./useRtfExport";
import { SHOW_ROW_SPAN_UI } from "./rowSpanFeature";
import { FindPanel, type FindMatch } from "./FindPanel";
import { EditorErrorBoundary } from "./EditorErrorBoundary";
import {
  copyShareUrl,
  editorStateToShareLocation,
  type EditorScreen,
  type ShareLocation,
} from "./shareLocation";

const SYNC_LABEL: Record<string, string> = {
  offline: "ingen forbindelse",
  connecting: "forbinder…",
  online: "online",
  error: "kan ikke gemme",
};

const LICENSE_LABEL: Record<string, string> = {
  ok: "ok",
  grace: "henstand",
  readonly: "skrivebeskyttet",
};

function ConnectionBanner({
  kind,
  onExport,
}: {
  kind: "offline" | "error";
  onExport: () => void;
}) {
  const lostNet = kind === "offline";
  return (
    <div className="sm-connection-banner sm-no-print" role="alert">
      <p>
        <strong>
          {lostNet
            ? "Ingen internetforbindelse"
            : "Kunne ikke gemme på serveren"}
        </strong>
        {lostNet
          ? " Ændringer bliver ikke gemt, før nettet er tilbage. Luk ikke fanen — så mistes det, du har skrevet siden sidste gem. Vent med at skrive mere, eller eksporter en kopi nu. Når forbindelsen er der igen, gemmes der automatisk."
          : " Luk ikke fanen. Der forsøges igen automatisk, når serveren svarer. Eksporter gerne en kopi, så du har teksten lokalt."}
      </p>
      <button type="button" onClick={onExport}>
        Eksporter en kopi
      </button>
    </div>
  );
}

export interface ScriptEditorProps {
  document: ScriptDocument;
  userId: Id;
  displayName: string;
  activeEpisodeId: Id | null;
  activeSceneId: Id | null;
  view: EpisodeView;
  presence?: PresenceUser[];
  syncStatus?: "offline" | "connecting" | "online" | "error";
  /**
   * True for the hosted web app: offline/error means edits are not on the server.
   * Desktop keeps a local copy, so the warning banner is skipped.
   */
  cloudBacked?: boolean;
  licenseStatus?: "ok" | "grace" | "readonly";
  onSelectScene: (sceneId: Id) => void;
  onSelectEpisode?: (episodeId: Id) => void;
  onChangeView: (view: EpisodeView) => void;
  onAddScene: () => void;
  onDeleteScene?: (sceneId: Id) => void;
  onArchiveScene?: (
    sceneId: Id,
    options: {
      mode: ArchiveSceneMode;
      replaceArchivedSceneId?: Id;
      label?: string;
    },
  ) => void;
  onDuplicateScene?: (sceneId: Id) => void;
  onCopySceneToEpisode?: (sceneId: Id, destEpisodeId: Id) => void;
  onMoveSceneToEpisode?: (sceneId: Id, destEpisodeId: Id) => void;
  onCopySceneToProduction?: (
    sceneId: Id,
    destProductionId: Id,
    destEpisodeId: Id,
  ) => void;
  onMoveSceneToProduction?: (
    sceneId: Id,
    destProductionId: Id,
    destEpisodeId: Id,
  ) => void;
  onLoadProductionEpisodes?: (
    productionId: Id,
  ) => Promise<{ id: Id; label: string }[]>;
  onSplitScene?: (sceneId: Id, atCueId: Id) => void;
  onMergeScenes?: (
    keepSceneId: Id,
    otherSceneId: Id,
    identity: { title: string; number?: ItemNumber },
  ) => void;
  onAddSeason?: () => void;
  /** Called with the focused season when seasons are enabled. */
  onAddEpisode?: (seasonId?: Id | null) => void;
  onDeleteEpisode?: (episodeId: Id) => void;
  onDuplicateEpisode?: (episodeId: Id) => void;
  onExportEpisodeBackup?: (episodeId: Id) => void;
  onImportEpisodeBackup?: () => void;
  onCopyEpisodeToProduction?: (episodeId: Id, destProductionId: Id) => void;
  onMoveEpisodeToProduction?: (episodeId: Id, destProductionId: Id) => void;
  onMoveEpisodeToSeason?: (episodeId: Id, destSeasonId: Id) => void;
  otherProductions?: { id: Id; title: string }[];
  onReorderEpisodes?: (orderedIds: Id[]) => void;
  onSetUseSeasons?: (enabled: boolean) => void;
  onRestoreTrashEpisode?: (episodeId: Id) => void;
  onPurgeTrashEpisode?: (episodeId: Id) => void;
  onRestoreTrashScene?: (sceneId: Id) => void;
  onPurgeTrashScene?: (sceneId: Id) => void;
  onEmptyTrash?: () => void;
  onRestoreArchiveScene?: (
    archivedSceneId: Id,
    options: { mode: RestoreArchiveMode },
  ) => void;
  onPurgeArchiveScene?: (archivedSceneId: Id) => void;
  onPurgeArchiveEpisode?: (episodeId: Id) => void;
  onEmptyArchive?: () => void;
  onRenameSeason?: (seasonId: Id, title: string) => void;
  onMoveScene: (
    sceneId: Id,
    beforeSceneId: Id | null,
    options?: { renumber?: boolean },
  ) => void;
  onChangeSceneNumberingMode: (mode: SceneNumberingMode) => void;
  onChangeEpisodeNumberingMode?: (mode: SceneNumberingMode) => void;
  onChangeEpisodeNumber?: (episodeId: Id, number: string) => void;
  onAddDialogue: (sceneId: Id) => void;
  onCueFocus: (cueId: Id, column: ScriptColumn) => void;
  onCueBlur: (cueId: Id, column: ScriptColumn) => void;
  onCueChange: (cueId: Id, column: ScriptColumn, content: string) => void;
  onMoveCueBlock: (cueId: Id, beforeCueId: Id | null) => void;
  onNewPair: (
    afterCueId: Id,
    column: ScriptColumn,
    flushedContent: string,
    place?: "after" | "before",
  ) => Id | null;
  onDeletePair: (cueId: Id) => void;
  onDeletePairs?: (cueIds: Id[]) => void;
  onSetVisualSpan: (
    cueId: Id,
    span: number,
    coveredVisual?: "clear" | "merge",
  ) => void;
  onSetAudioSpan: (
    cueId: Id,
    span: number,
    coveredAudio?: "clear" | "merge",
  ) => void;
  onTogglePrintBorderBelow: (cueId: Id) => void;
  onRenameProduction: (title: string) => void;
  onRenameEpisode: (episodeId: Id, title: string) => void;
  onRenameDisplayName: (name: string) => void;
  onSceneMetaChange: (
    sceneId: Id,
    patch: Partial<{
      title: string;
      description: string;
      durationSeconds: number;
      number: ItemNumber;
      headerBackground: string;
      heading: string;
      headingKind: "int" | "ext" | undefined;
      headingLocation: string;
      headingTime: string;
      headingNote: string;
    }>,
  ) => void;
  onAddCastMember: (name: string) => void;
  onUpdateCastMember: (
    memberId: Id,
    patch: Partial<
      Pick<CastMember, "name" | "color" | "showNameAboveLines">
    >,
  ) => void;
  onRemoveCastMember: (memberId: Id) => void;
  onAddComment: (
    cueId: Id,
    column: ScriptColumn,
    body: string,
    range?: { from: number; to: number; quote: string },
  ) => void;
  onResolveComment: (commentId: Id) => void;
  onReopenComment?: (commentId: Id) => void;
  onRemoveComment: (commentId: Id) => void;
  onSetCharacter: (
    cueId: Id,
    column: ScriptColumn,
    characterName: string | null,
  ) => void;
  onChangeCrewRoles: (sceneId: Id, roles: SceneCrewRole[]) => void;
  onChangeEpisodeCrewRoles: (roles: SceneCrewRole[]) => void;
  /** Remember a finished custom job title (blur). */
  onRememberJobTitle?: (title: string) => void;
  /** Remove a custom job-title chip. */
  onRemoveJobTitle?: (title: string) => void;
  onToggleSeasonCrewPerson?: (
    episodeId: Id,
    jobTitle: string,
    person: { name: string; detail?: string },
    onSeason: boolean,
  ) => void;
  onDeactivateSeasonCrewPerson?: (
    episodeId: Id,
    jobTitle: string,
    person: { name: string; detail?: string },
    mode: CrewSeasonDeactivateMode,
  ) => void;
  onRemoveSeasonCrewPerson?: (
    episodeId: Id,
    jobTitle: string,
    person: { name: string; detail?: string },
  ) => void;
  onChangeProgramTexts: (texts: ProgramText[]) => void;
  onCueNoteChange: (cueId: Id, columnId: Id, text: string) => void;
  onSceneNoteChange?: (sceneId: Id, columnId: Id, text: string) => void;
  onChangeNoteColumns: (columns: NoteColumnDef[]) => void;
  onRemoveNoteColumn: (
    columnId: Id,
    scope: RemoveNoteColumnScope,
  ) => void;
  onCameraMetaChange: (
    cueId: Id,
    patch: {
      cameraNumber?: number | null;
      cameraNote?: string;
      positionNote?: string;
    },
  ) => void;
  onToggleStatusStep: (sceneId: Id, stepId: Id) => void;
  onToggleEpisodeStatusStep?: (stepId: Id) => void;
  onChangeStatusSteps: (steps: StatusStepDef[]) => void;
  onChangeSceneStatusSteps: (
    sceneId: Id,
    steps: StatusStepDef[] | null,
  ) => void;
  onChangeEpisodeStatusSteps?: (steps: StatusStepDef[]) => void;
  focusCell?: CellRef | null;
  onFocusCellHandled?: (cell: CellRef) => void;
  /** Bump after undo/redo so TipTap matches the document without remounting. */
  contentResetKey?: number | string;
  readOnly?: boolean;
  /** Navigate back to the production list. */
  onGoHome?: () => void;
  /** Screen to open when this production is first mounted. */
  initialScreen?: EditorScreen;
  initialNoteColumnId?: Id | null;
  /**
   * Origin + path used when copying permalinks (web: current page, desktop:
   * hosted site). When omitted, copy-link actions are hidden.
   */
  shareLinkBase?: string;
  /** Keep the browser URL in sync with the current editor location. */
  onShareLocation?: (loc: ShareLocation) => void;
  /** Hosted API root — enables tilrettelæggermanus and remote settings sync. */
  apiBase?: string | null;
  onSubmitDirectorScene?: (
    originEpisodeId: Id,
    liveSceneId: Id,
    payload: DirectorScenePayload,
  ) => Promise<Id | null | void> | Id | null | void;
  /** Persist document mutations while editing an unlocked submitted scene. */
  onDirectorDocumentMutate?: (
    mutate: (doc: ScriptDocument) => ScriptDocument,
  ) => void;
  onInsertSubmittedIntoProgram?: (
    submittedSceneId: Id,
    options: { mode: RestoreArchiveMode },
  ) => void;
}

function buildDirectorArchiveBrowseDocument(
  program: ScriptDocument,
  liveSceneId: Id,
  archives: DirectorArchiveRow[],
): ScriptDocument {
  const live = program.scenes[liveSceneId];
  if (!live) return program;
  const scenes = { ...program.scenes };
  const cues = { ...program.cues };
  const comments = { ...(program.comments ?? {}) };
  for (const row of archives) {
    const oldToNew: Record<Id, Id> = {};
    const newCueIds: Id[] = [];
    for (const oldId of row.payload.scene.cueIds) {
      const newId = createId();
      oldToNew[oldId] = newId;
      newCueIds.push(newId);
      const c = row.payload.cues[oldId];
      if (c) {
        cues[newId] = { ...c, id: newId, sceneId: row.id };
      }
    }
    scenes[row.id] = {
      ...row.payload.scene,
      id: row.id,
      cueIds: newCueIds,
      archiveOfSceneId: liveSceneId,
      archivedAt: row.archivedAt,
      archiveLabel: row.archiveLabel ?? undefined,
      episodeId: live.episodeId,
      number: live.number,
      sortOrder: live.sortOrder,
    };
    for (const [, comment] of Object.entries(row.payload.comments ?? {})) {
      const newCueId = oldToNew[comment.cueId];
      if (!newCueId) continue;
      const newCommentId = createId();
      comments[newCommentId] = {
        ...comment,
        id: newCommentId,
        cueId: newCueId,
      };
    }
  }
  return { ...program, scenes, cues, comments };
}

export type { EditorScreen } from "./shareLocation";


export function ScriptEditor({
  document,
  userId,
  displayName,
  activeEpisodeId,
  activeSceneId,
  view,
  syncStatus = "offline",
  cloudBacked = false,
  licenseStatus = "ok",
  onSelectScene,
  onSelectEpisode,
  onChangeView,
  onAddScene,
  onDeleteScene,
  onArchiveScene,
  onDuplicateScene,
  onCopySceneToEpisode,
  onMoveSceneToEpisode,
  onCopySceneToProduction,
  onMoveSceneToProduction,
  onLoadProductionEpisodes,
  onSplitScene,
  onMergeScenes,
  onAddSeason,
  onAddEpisode,
  onDeleteEpisode,
  onDuplicateEpisode,
  onExportEpisodeBackup,
  onImportEpisodeBackup,
  onCopyEpisodeToProduction,
  onMoveEpisodeToProduction,
  onMoveEpisodeToSeason,
  otherProductions,
  onReorderEpisodes,
  onSetUseSeasons,
  onRestoreTrashEpisode,
  onPurgeTrashEpisode,
  onRestoreTrashScene,
  onPurgeTrashScene,
  onEmptyTrash,
  onRestoreArchiveScene,
  onPurgeArchiveScene,
  onPurgeArchiveEpisode,
  onEmptyArchive,
  onMoveScene,
  onChangeSceneNumberingMode,
  onChangeEpisodeNumberingMode,
  onChangeEpisodeNumber,
  onAddDialogue,
  onCueFocus,
  onCueBlur,
  onCueChange,
  onMoveCueBlock,
  onNewPair,
  onDeletePair,
  onDeletePairs,
  onSetVisualSpan,
  onSetAudioSpan,
  onTogglePrintBorderBelow,
  onRenameProduction,
  onRenameEpisode,
  onRenameDisplayName,
  onSceneMetaChange,
  onAddCastMember,
  onUpdateCastMember,
  onRemoveCastMember,
  onAddComment,
  onResolveComment,
  onReopenComment,
  onRemoveComment,
  onSetCharacter,
  onChangeCrewRoles,
  onChangeEpisodeCrewRoles,
  onRememberJobTitle,
  onRemoveJobTitle,
  onToggleSeasonCrewPerson,
  onDeactivateSeasonCrewPerson,
  onRemoveSeasonCrewPerson,
  onChangeProgramTexts,
  onCueNoteChange,
  onSceneNoteChange,
  onChangeNoteColumns,
  onRemoveNoteColumn,
  onCameraMetaChange,
  onToggleStatusStep,
  onToggleEpisodeStatusStep,
  onChangeStatusSteps,
  onChangeSceneStatusSteps,
  onChangeEpisodeStatusSteps,
  focusCell,
  onFocusCellHandled,
  contentResetKey = 0,
  readOnly = false,
  onGoHome,
  initialScreen = "dashboard",
  initialNoteColumnId = null,
  shareLinkBase,
  onShareLocation,
  apiBase,
  onSubmitDirectorScene,
  onDirectorDocumentMutate,
  onInsertSubmittedIntoProgram,
}: ScriptEditorProps) {
  const [settings, setSettings, settingsActions] = useEditorSettings(userId, {
    apiBase: apiBase ?? null,
  });
  const [directorMode, setDirectorMode] = useState(false);
  const [editorScreen, setEditorScreen] =
    useState<EditorScreen>(initialScreen);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSection, setHelpSection] = useState<HelpSectionId | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [showResolvedComments, setShowResolvedComments] = useState(false);
  const [cellSelectMode, setCellSelectMode] = useState(false);
  // Flet rækker / Højde: SHOW_ROW_SPAN_UI i rowSpanFeature.ts
  const [rowSpanMode, setRowSpanMode] = useState(false);
  const [activeEditor, setActiveEditor] = useState<Editor | null>(null);
  const [pendingSceneMove, setPendingSceneMove] = useState<{
    sceneId: Id;
    beforeSceneId: Id | null;
  } | null>(null);
  const [numberingNotice, setNumberingNotice] = useState<
    "scene" | "episode" | null
  >(null);
  const [colorMenu, setColorMenu] = useState<{
    sceneId: Id;
    x: number;
    y: number;
  } | null>(null);
  const [transferSceneId, setTransferSceneId] = useState<Id | null>(null);
  const [archiveSceneId, setArchiveSceneId] = useState<Id | null>(null);
  const [browseArchiveSceneId, setBrowseArchiveSceneId] = useState<Id | null>(
    null,
  );
  const [directorArchiveSceneId, setDirectorArchiveSceneId] =
    useState<Id | null>(null);
  const [browseDirectorArchiveSceneId, setBrowseDirectorArchiveSceneId] =
    useState<Id | null>(null);
  const [pendingInsertSubmittedSceneId, setPendingInsertSubmittedSceneId] =
    useState<Id | null>(null);
  const [unlockDirectorSceneId, setUnlockDirectorSceneId] =
    useState<Id | null>(null);
  const [splitPickSceneId, setSplitPickSceneId] = useState<Id | null>(null);
  const [pendingMerge, setPendingMerge] = useState<{
    keepSceneId: Id;
    otherSceneId: Id;
  } | null>(null);
  const lastCueIdRef = useRef<Id | null>(null);
  const [noteColumnId, setNoteColumnId] = useState<Id | null>(
    initialNoteColumnId,
  );
  const [pendingRemoveNoteColumnId, setPendingRemoveNoteColumnId] =
    useState<Id | null>(null);
  const [findOpen, setFindOpen] = useState(false);
  const [findFocusCell, setFindFocusCell] = useState<CellRef | null>(null);
  const [findFocusNote, setFindFocusNote] = useState<{
    cueId: Id;
    query: string;
    occurrence: number;
    nonce: number;
  } | null>(null);
  const findSelectNonceRef = useRef(0);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const copyToastTimerRef = useRef(0);
  const [windowActive, setWindowActive] = useState(
    () =>
      typeof window === "undefined" ||
      (window.document.hasFocus() &&
        window.document.visibilityState === "visible"),
  );
  const {
    exportManual,
    exportAudioManual,
    exportDocxManual,
    exportAudioDocxManual,
    chooseAutoFile,
    lastSavedAt,
    exportError,
    canUseFileSystemAccess,
  } = useRtfExport(document, activeEpisodeId, settings.autoExportRtf, settings);
  const closeSceneColorMenu = useCallback(() => setColorMenu(null), []);

  useEffect(() => {
    const sync = () => {
      setWindowActive(
        window.document.hasFocus() &&
          window.document.visibilityState === "visible",
      );
    };
    window.addEventListener("blur", sync);
    window.addEventListener("focus", sync);
    window.document.addEventListener("visibilitychange", sync);
    sync();
    return () => {
      window.removeEventListener("blur", sync);
      window.removeEventListener("focus", sync);
      window.document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  useEffect(() => {
    if (view !== "script" && view !== "sources") setActiveEditor(null);
  }, [view]);

  useEffect(() => {
    setActiveEditor((ed) => (ed && !ed.isDestroyed ? ed : null));
  }, [contentResetKey]);

  const setLiveActiveEditor = useCallback((ed: Editor | null) => {
    setActiveEditor(ed && !ed.isDestroyed ? ed : null);
  }, []);

  const savedScrollRef = useRef<{ x: number; y: number } | null>(null);
  const prevContentResetKeyRef = useRef(contentResetKey);
  if (prevContentResetKeyRef.current !== contentResetKey) {
    savedScrollRef.current = { x: window.scrollX, y: window.scrollY };
    prevContentResetKeyRef.current = contentResetKey;
  }
  useLayoutEffect(() => {
    const saved = savedScrollRef.current;
    if (!saved) return;
    savedScrollRef.current = null;
    const restore = () => window.scrollTo(saved.x, saved.y);
    restore();
    const id = window.requestAnimationFrame(restore);
    return () => window.cancelAnimationFrame(id);
  }, [contentResetKey]);

  const director = useDirectorWorkspace({
    enabled: directorMode,
    apiBase,
    projectId: document.production.id,
    episodeId: activeEpisodeId,
    document,
    onDocumentMutate: onDirectorDocumentMutate,
  });

  const episodes = listEpisodes(document);
  const episode =
    (activeEpisodeId && document.episodes[activeEpisodeId]) ||
    episodes[0] ||
    null;
  const viewDoc = directorMode ? director.mergedDocument : document;
  const season =
    episode?.seasonId && document.seasons[episode.seasonId]
      ? document.seasons[episode.seasonId]
      : null;
  const scenes = episode ? getEpisodeScenes(viewDoc, episode.id) : [];
  const scene =
    (activeSceneId && viewDoc.scenes[activeSceneId]) || scenes[0] || null;
  /** Scene the user has actually selected — not the fallback used for manus layout. */
  const explicitScene = (() => {
    if (!activeSceneId || !episode) return null;
    const selected = viewDoc.scenes[activeSceneId];
    if (!selected || selected.episodeId !== episode.id) return null;
    return selected;
  })();
  /** Scene actions only on Manus/Noter — not Sceneliste/Rundown/etc. */
  const directorSceneFocus =
    Boolean(explicitScene) &&
    (workspaceView(view) === "script" || workspaceView(view) === "sources");
  const cues = scene ? getSceneCues(viewDoc, scene.id) : [];
  const directorSceneRowClass = directorMode
    ? director.sceneRowClass
    : undefined;
  const activeSceneSheetClass =
    scene && directorSceneRowClass?.(scene.id)
      ? directorSceneRowClass(scene.id)
      : "";
  const scrolledSceneIdRef = useRef<Id | null>(null);
  const editorSceneRef = useRef<Id | null>(scene?.id ?? null);

  // Drop stale TipTap refs *during render* when the scene changes — before
  // FormatToolbar reads editor.schema. Cleared in useLayoutEffect is too late:
  // the sheet remount destroys editors first, then toolbar still holds them.
  if (editorSceneRef.current !== (scene?.id ?? null)) {
    editorSceneRef.current = scene?.id ?? null;
    if (activeEditor !== null) {
      setActiveEditor(null);
    }
  }

  // Same as TabNav: land at top of the new scene. One layout scroll is enough.
  useLayoutEffect(() => {
    const id = scene?.id ?? null;
    if (!id || scrolledSceneIdRef.current === id) return;
    const isFirst = scrolledSceneIdRef.current === null;
    scrolledSceneIdRef.current = id;
    if (isFirst) return;

    setFindFocusCell(null);
    setFindFocusNote(null);
    window.scrollTo(0, 0);
  }, [scene?.id]);

  useEffect(() => {
    if (view !== "script") {
      setCellSelectMode(false);
      setRowSpanMode(false);
    }
  }, [view]);

  const seasonTitle = productionShowsSeasonChrome(
    document.production,
    season,
  )
    ? season?.title
    : undefined;

  const episodeDisplayTitle = episode
    ? formatEpisodeDisplayTitle(episode, settings.showEpisodeNumbers)
    : "";
  const vaultKind = episode ? getEpisodeVaultKind(document, episode) : null;
  const vaultLabel =
    vaultKind === "trash"
      ? "Papirkurv"
      : vaultKind === "archive"
        ? "Arkiv"
        : vaultKind === "submitted"
          ? "Afleverede scener"
          : null;

  const currentView = workspaceView(view);
  const manuscriptScrollYRef = useRef(0);
  /** Window scroll is shared — remember dashboard Y so episode scroll does not leak back. */
  const dashboardScrollYRef = useRef(0);
  const prevEditorScreenRef = useRef(editorScreen);

  const goToDashboard = useCallback(() => {
    setDirectorMode(false);
    setEditorScreen("dashboard");
  }, []);

  /** Manus ↔ Noter keep scroll; other tab changes go to top. */
  const changeView = useCallback(
    (next: EpisodeView) => {
      const from = workspaceView(view);
      const to = workspaceView(next);
      const keepScroll =
        (from === "script" || from === "sources") &&
        (to === "script" || to === "sources") &&
        from !== to;
      if (keepScroll) {
        manuscriptScrollYRef.current = window.scrollY;
      }
      onChangeView(next);
      if (keepScroll) {
        const y = manuscriptScrollYRef.current;
        const restore = () => window.scrollTo(0, y);
        restore();
        requestAnimationFrame(() => {
          restore();
          requestAnimationFrame(restore);
        });
      } else if (from !== to) {
        window.scrollTo(0, 0);
      }
    },
    [onChangeView, view],
  );

  useLayoutEffect(() => {
    const prev = prevEditorScreenRef.current;
    prevEditorScreenRef.current = editorScreen;
    if (prev === editorScreen) return;

    if (prev === "dashboard" && editorScreen === "episode") {
      // Captured in openEpisodeFromDash before setState; land at top of episode.
      window.scrollTo(0, 0);
      return;
    }

    if (prev === "episode" && editorScreen === "dashboard") {
      const y = dashboardScrollYRef.current;
      const restore = () => window.scrollTo(0, y);
      restore();
      const id = window.requestAnimationFrame(() => {
        restore();
        window.requestAnimationFrame(restore);
      });
      return () => window.cancelAnimationFrame(id);
    }
  }, [editorScreen]);

  useEffect(() => {
    onShareLocation?.(
      editorStateToShareLocation({
        productionId: document.production.id,
        screen: editorScreen,
        episodeId: episode?.id ?? null,
        view: currentView,
        sceneId: scene?.id ?? null,
        noteColumnId,
      }),
    );
  }, [
    currentView,
    document.production.id,
    editorScreen,
    episode?.id,
    noteColumnId,
    onShareLocation,
    scene?.id,
  ]);

  useEffect(() => {
    return () => {
      if (copyToastTimerRef.current) {
        window.clearTimeout(copyToastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (editorScreen === "dashboard") {
      window.document.title = DEFAULT_BROWSER_TITLE;
      return;
    }
    window.document.title = formatBrowserDocumentTitle(
      episodeDisplayTitle,
      currentView,
    );
  }, [currentView, editorScreen, episodeDisplayTitle]);

  useEffect(() => {
    return () => {
      window.document.title = DEFAULT_BROWSER_TITLE;
    };
  }, []);

  const requestMoveScene = useCallback(
    (sceneId: Id, beforeSceneId: Id | null) => {
      if (
        readOnly ||
        settings.viewOnly ||
        licenseStatus === "readonly" ||
        vaultKind ||
        directorMode
      ) {
        return;
      }
      setPendingSceneMove({ sceneId, beforeSceneId });
    },
    [directorMode, licenseStatus, readOnly, settings.viewOnly, vaultKind],
  );

  useEffect(() => {
    if (editorScreen !== "episode" || currentView !== "script") {
      setSplitPickSceneId(null);
    }
  }, [currentView, editorScreen]);

  useEffect(() => {
    if (showPrint) setSplitPickSceneId(null);
  }, [showPrint]);

  useEffect(() => {
    if (!splitPickSceneId) return;
    if (activeSceneId && splitPickSceneId !== activeSceneId) {
      setSplitPickSceneId(null);
    }
  }, [activeSceneId, splitPickSceneId]);

  useEffect(() => {
    if (!splitPickSceneId) return;
    setActiveEditor(null);
    const el = window.document.activeElement;
    if (el instanceof HTMLElement) el.blur();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        setSplitPickSceneId(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [splitPickSceneId]);

  const handleFindJump = useCallback(
    (match: FindMatch) => {
      onSelectScene(match.sceneId);
      findSelectNonceRef.current += 1;
      const nonce = findSelectNonceRef.current;
      if (match.kind === "script") {
        onChangeView("script");
        setFindFocusNote(null);
        setFindFocusCell({
          cueId: match.cueId,
          column: match.column as ScriptColumn,
          selectQuery: match.query,
          selectOccurrence: match.occurrence,
          selectNonce: nonce,
        });
      } else {
        onChangeView("sources");
        if (match.column !== "legacy") {
          setNoteColumnId(match.column);
        }
        setFindFocusCell(null);
        setFindFocusNote({
          cueId: match.cueId,
          query: match.query,
          occurrence: match.occurrence,
          nonce,
        });
      }
    },
    [onChangeView, onSelectScene],
  );

  useEffect(() => {
    if (editorScreen !== "episode") return;
    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const key = e.key;
      if (key === "f" || key === "F") {
        e.preventDefault();
        setFindOpen(true);
        return;
      }
      if (key === "=" || key === "+") {
        e.preventDefault();
        setSettings({
          episodeZoom: clampEpisodeZoom(settings.episodeZoom + 0.1),
        });
        return;
      }
      if (key === "-" || key === "_") {
        e.preventDefault();
        setSettings({
          episodeZoom: clampEpisodeZoom(settings.episodeZoom - 0.1),
        });
        return;
      }
      if (key === "0") {
        e.preventDefault();
        setSettings({ episodeZoom: 1 });
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editorScreen, setSettings, settings.episodeZoom]);

  const editorRootClass = `minproduktion-editor${
    windowActive ? "" : " sm-window-inactive"
  }`;

  if (!episode) {
    return <div className={editorRootClass}>Ingen episode indlæst.</div>;
  }

  const baseReadOnly =
    readOnly || settings.viewOnly || licenseStatus === "readonly";
  // Vault browse locks the episode workspace only — not the production dashboard.
  const effectiveReadOnly = baseReadOnly || Boolean(vaultKind);
  const settingsReadOnly = licenseStatus === "readonly";
  const splitPicking = Boolean(splitPickSceneId);
  const activeSceneHasFork = Boolean(
    explicitScene && director.hasFork(explicitScene.id),
  );
  const activeSceneSubmitLocked = Boolean(
    explicitScene && director.isSubmitLocked(explicitScene.id),
  );
  const activeSceneEditsSubmitted = Boolean(
    explicitScene && director.editsSubmittedVault(explicitScene.id),
  );
  const activeSubmitState = explicitScene
    ? director.getSubmitState(explicitScene.id)
    : null;
  const directorSceneLocked =
    directorMode &&
    Boolean(explicitScene) &&
    (!director.hasFork(explicitScene!.id) || activeSceneSubmitLocked);
  const editLocked =
    effectiveReadOnly || splitPicking || directorSceneLocked;

  function cueSceneId(cueId: Id): Id | null {
    return viewDoc.cues[cueId]?.sceneId ?? null;
  }

  function directorMutate(
    sceneId: Id,
    mutate: (doc: ScriptDocument) => ScriptDocument,
  ): boolean {
    if (!directorMode || !director.hasFork(sceneId)) return false;
    if (director.isSubmitLocked(sceneId)) return false;
    return director.applyMutation(sceneId, mutate) !== null;
  }

  function handleSceneMetaChange(
    sceneId: Id,
    patch: Parameters<ScriptEditorProps["onSceneMetaChange"]>[1],
  ) {
    if (directorMode) {
      if (!director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) => updateSceneMeta(prev, sceneId, patch));
      return;
    }
    changeSceneMeta(sceneId, patch);
  }

  function handleCueChange(
    cueId: Id,
    column: ScriptColumn,
    content: string,
  ) {
    const sceneId = cueSceneId(cueId);
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) =>
        updateCueSide(prev, cueId, column, content, userId),
      );
      return;
    }
    onCueChange(cueId, column, content);
  }

  function handleMoveCueBlock(cueId: Id, beforeCueId: Id | null) {
    const sceneId = cueSceneId(cueId) ?? scene?.id;
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) =>
        moveCueBlock(prev, sceneId, cueId, beforeCueId),
      );
      return;
    }
    onMoveCueBlock(cueId, beforeCueId);
  }

  function handleNewPair(
    afterCueId: Id,
    column: ScriptColumn,
    flushedContent: string,
    place: "after" | "before" = "after",
  ): Id | null {
    const sceneId = cueSceneId(afterCueId) ?? scene?.id;
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId)) return null;
      let newCueId: Id | null = null;
      director.applyMutation(sceneId, (prev) => {
        const cue = prev.cues[afterCueId];
        if (!cue) return prev;
        const flushed = updateCueSide(
          prev,
          afterCueId,
          column,
          flushedContent,
          userId,
        );
        const inserted =
          place === "before"
            ? insertCueBefore(flushed, sceneId, afterCueId)
            : insertCueAfter(flushed, sceneId, afterCueId);
        newCueId = inserted.cueId;
        return inserted.doc;
      });
      return newCueId;
    }
    return onNewPair(afterCueId, column, flushedContent, place);
  }

  function handleDeletePair(cueId: Id) {
    const sceneId = cueSceneId(cueId) ?? scene?.id;
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) => removeCue(prev, sceneId, cueId));
      return;
    }
    onDeletePair(cueId);
  }

  function handleDeletePairs(cueIds: Id[]) {
    const sceneId = scene?.id;
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId) || cueIds.length === 0) {
        return;
      }
      directorMutate(sceneId, (prev) => {
        let doc = prev;
        const sc = doc.scenes[sceneId];
        if (!sc) return prev;
        const ordered = [...sc.cueIds]
          .filter((id) => cueIds.includes(id))
          .reverse();
        for (const id of ordered) {
          doc = removeCue(doc, sceneId, id);
        }
        return doc;
      });
      return;
    }
    onDeletePairs?.(cueIds);
  }

  function handleSetVisualSpan(
    cueId: Id,
    span: number,
    coveredVisual?: "clear" | "merge",
  ) {
    const sceneId = cueSceneId(cueId);
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) =>
        setVisualRowSpan(prev, cueId, span, { coveredVisual }),
      );
      return;
    }
    onSetVisualSpan(cueId, span, coveredVisual);
  }

  function handleSetAudioSpan(
    cueId: Id,
    span: number,
    coveredAudio?: "clear" | "merge",
  ) {
    const sceneId = cueSceneId(cueId);
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) =>
        setAudioRowSpan(prev, cueId, span, { coveredAudio }),
      );
      return;
    }
    onSetAudioSpan(cueId, span, coveredAudio);
  }

  function handleTogglePrintBorderBelow(cueId: Id) {
    const sceneId = cueSceneId(cueId);
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) => {
        const cue = prev.cues[cueId];
        if (!cue) return prev;
        return setCuePrintBorderBelow(
          prev,
          cueId,
          !Boolean(cue.printBorderBelow),
        );
      });
      return;
    }
    onTogglePrintBorderBelow(cueId);
  }

  function handleSetCharacter(
    cueId: Id,
    column: ScriptColumn,
    characterName: string | null,
  ) {
    const sceneId = cueSceneId(cueId);
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) =>
        setCueCharacter(prev, cueId, column, characterName, userId),
      );
      return;
    }
    onSetCharacter(cueId, column, characterName);
  }

  function handleCueNoteChange(cueId: Id, columnId: Id, text: string) {
    const sceneId = cueSceneId(cueId);
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) => setCueNote(prev, cueId, columnId, text));
      return;
    }
    onCueNoteChange(cueId, columnId, text);
  }

  function handleSceneNoteChange(sceneId: Id, columnId: Id, text: string) {
    if (directorMode) {
      if (!director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) =>
        setSceneNote(prev, sceneId, columnId, text),
      );
      return;
    }
    onSceneNoteChange?.(sceneId, columnId, text);
  }

  function handleCameraMetaChange(
    cueId: Id,
    patch: {
      cameraNumber?: number | null;
      cameraNote?: string;
      positionNote?: string;
    },
  ) {
    const sceneId = cueSceneId(cueId);
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) => setCueCameraMeta(prev, cueId, patch));
      return;
    }
    onCameraMetaChange(cueId, patch);
  }

  function handleAddComment(
    cueId: Id,
    column: ScriptColumn,
    body: string,
    range?: { from: number; to: number; quote: string },
  ) {
    const sceneId = cueSceneId(cueId);
    if (directorMode) {
      if (!sceneId || !director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) =>
        addComment(prev, {
          cueId,
          column,
          body,
          authorId: userId,
          authorName: displayName,
          from: range?.from,
          to: range?.to,
          quote: range?.quote,
        }).doc,
      );
      return;
    }
    onAddComment(cueId, column, body, range);
  }

  function handleResolveComment(commentId: Id) {
    if (directorMode && scene?.id && director.hasFork(scene.id)) {
      directorMutate(scene.id, (prev) =>
        updateComment(prev, commentId, { resolved: true }),
      );
      return;
    }
    onResolveComment(commentId);
  }

  function handleRemoveComment(commentId: Id) {
    if (directorMode && scene?.id && director.hasFork(scene.id)) {
      directorMutate(scene.id, (prev) => removeComment(prev, commentId));
      return;
    }
    onRemoveComment(commentId);
  }

  function handleToggleStatusStep(sceneId: Id, stepId: Id) {
    if (directorMode) {
      if (!director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) =>
        toggleSceneStatusStep(prev, sceneId, stepId),
      );
      return;
    }
    onToggleStatusStep(sceneId, stepId);
  }

  function handleChangeSceneStatusSteps(
    sceneId: Id,
    steps: StatusStepDef[] | null,
  ) {
    if (directorMode) {
      if (!director.hasFork(sceneId)) return;
      directorMutate(sceneId, (prev) =>
        setSceneStatusSteps(prev, sceneId, steps),
      );
      return;
    }
    onChangeSceneStatusSteps(sceneId, steps);
  }

  function handleAddDialogue(sceneId: Id) {
    if (directorMode) {
      if (!director.hasFork(sceneId)) return;
      const cast = [...(document.production.castMembers ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      );
      let name: string | null = null;
      if (cast.length > 0) {
        const list = cast.map((m, i) => `${i + 1}. ${m.name}`).join("\n");
        const answer = window.prompt(
          `Vælg medvirkende (nummer) eller skriv navn:\n${list}`,
          cast[0]!.name,
        );
        if (!answer) return;
        const trimmed = answer.trim();
        const asNum = Number(trimmed);
        if (
          Number.isInteger(asNum) &&
          asNum >= 1 &&
          asNum <= cast.length
        ) {
          name = cast[asNum - 1]!.name;
        } else {
          name = trimmed;
        }
      } else {
        name = window.prompt("Karakternavn", "ANNA");
        if (!name?.trim()) return;
        name = name.trim();
      }
      directorMutate(sceneId, (prev) => addDialogueCue(prev, sceneId, name!).doc);
      return;
    }
    onAddDialogue(sceneId);
  }
  const numberingMode: SceneNumberingMode =
    episode.sceneNumberingMode === "custom" ? "custom" : "sequential";

  function changeSceneMeta(
    sceneId: Id,
    patch: Parameters<ScriptEditorProps["onSceneMetaChange"]>[1],
  ) {
    if (numberingMode !== "custom" && patch.number !== undefined) {
      const current = document.scenes[sceneId]?.number;
      if (current !== undefined && String(current) !== String(patch.number)) {
        setNumberingNotice("scene");
      }
    }
    onSceneMetaChange(sceneId, patch);
  }

  function changeEpisodeNumber(episodeId: Id, number: string) {
    const current = document.episodes[episodeId];
    if (!current || !onChangeEpisodeNumber) return;
    if (
      document.production.episodeNumberingMode !== "custom" &&
      String(current.number) !== number
    ) {
      setNumberingNotice("episode");
    }
    onChangeEpisodeNumber(episodeId, number);
  }

  const productionNoteColumns = getNoteColumns(document.production);
  const noteColumns = getVisibleNoteColumns(document, episode?.id ?? null);
  const activeNoteColumnId = noteColumns.some((c) => c.id === noteColumnId)
    ? noteColumnId
    : (noteColumns[0]?.id ?? null);
  const pendingRemoveNoteColumn = pendingRemoveNoteColumnId
    ? productionNoteColumns.find((c) => c.id === pendingRemoveNoteColumnId) ??
      null
    : null;
  const colorMenuScene = colorMenu
    ? viewDoc.scenes[colorMenu.sceneId]
    : null;

  function confirmSceneMove(renumber: boolean) {
    if (!pendingSceneMove) return;
    const { sceneId, beforeSceneId } = pendingSceneMove;
    setPendingSceneMove(null);
    onMoveScene(sceneId, beforeSceneId, { renumber });
  }

  function openSceneColorMenu(
    sceneId: Id,
    e: {
      clientX: number;
      clientY: number;
      preventDefault: () => void;
      stopPropagation: () => void;
    },
  ) {
    if (effectiveReadOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setColorMenu({ sceneId, x: e.clientX, y: e.clientY });
  }

  function handleCueFocus(cueId: Id, column: ScriptColumn) {
    lastCueIdRef.current = cueId;
    onCueFocus(cueId, column);
  }

  function episodeChoiceLabel(ep: (typeof episodes)[number]): string {
    const s = ep.seasonId ? document.seasons[ep.seasonId] : null;
    const title = formatEpisodeDisplayTitle(ep, settings.showEpisodeNumbers);
    if (s && productionShowsSeasonChrome(document.production, s)) {
      const st = s.title.trim() || `Sæson ${s.number}`;
      return `${st} · ${title}`;
    }
    return title;
  }

  async function handleToggleAutoExport(enabled: boolean) {
    if (enabled) {
      if (!canUseFileSystemAccess) {
        window.alert(
          "Din browser understøtter ikke løbende filgemning. Brug Eksportér/Udskriv og vælg Gem RTF, eller åbn appen i Chrome/Edge.",
        );
        return;
      }
      const ok = await chooseAutoFile();
      if (!ok) return;
    }
    setSettings({ autoExportRtf: enabled });
  }

  function applySettingsPatch(
    patch: Parameters<typeof setSettings>[0],
  ) {
    if (patch.autoExportRtf === true) {
      void handleToggleAutoExport(true);
      return;
    }
    if (patch.autoExportRtf === false) {
      setSettings({ autoExportRtf: false });
      return;
    }
    if (patch.episodeZoom !== undefined) {
      setSettings({
        ...patch,
        episodeZoom: clampEpisodeZoom(patch.episodeZoom),
      });
      return;
    }
    setSettings(patch);
  }

  function openHelp(section?: HelpSectionId) {
    setHelpSection(section ?? "overview");
    setHelpOpen(true);
  }

  function flashCopied() {
    setCopyToast("Link kopieret");
    if (copyToastTimerRef.current) {
      window.clearTimeout(copyToastTimerRef.current);
    }
    copyToastTimerRef.current = window.setTimeout(() => {
      setCopyToast(null);
      copyToastTimerRef.current = 0;
    }, 1800);
  }

  async function copyShare(loc: ShareLocation) {
    if (!shareLinkBase) return;
    const ok = await copyShareUrl(shareLinkBase, loc);
    if (ok) flashCopied();
  }

  function copyTabLink(episodeId: Id, tab: EpisodeView = "timing") {
    void copyShare({
      productionId: document.production.id,
      episodeId,
      view: workspaceView(tab),
    });
  }

  function copySceneLink(sceneId: Id) {
    const sc = document.scenes[sceneId];
    if (!sc) return;
    void copyShare({
      productionId: document.production.id,
      episodeId: sc.episodeId,
      view: "script",
      sceneId,
    });
  }

  function copyNoteColumnLink(sceneId: Id, columnId: Id) {
    const sc = document.scenes[sceneId];
    if (!sc) return;
    void copyShare({
      productionId: document.production.id,
      episodeId: sc.episodeId,
      view: "sources",
      sceneId,
      noteColumnId: columnId,
    });
  }

  function openEpisodeFromDash(
    episodeId: Id,
    nextView: EpisodeView = "timing",
    sceneId?: Id,
  ) {
    setDirectorMode(false);
    dashboardScrollYRef.current = window.scrollY;
    onSelectEpisode?.(episodeId);
    if (sceneId) onSelectScene(sceneId);
    onChangeView(nextView);
    setEditorScreen("episode");
  }

  function openDirectorFromDash(
    episodeId: Id,
    nextView: EpisodeView = "timing",
    sceneId?: Id,
  ) {
    dashboardScrollYRef.current = window.scrollY;
    onSelectEpisode?.(episodeId);
    if (sceneId) onSelectScene(sceneId);
    onChangeView(nextView);
    setDirectorMode(true);
    setEditorScreen("episode");
  }

  async function handleCreateDirectorFork() {
    if (!explicitScene?.id) return;
    try {
      await director.createFork(explicitScene.id);
    } catch (err) {
      console.error(err);
      window.alert(
        err instanceof Error
          ? err.message
          : "Kunne ikke oprette din version.",
      );
    }
  }

  async function handleSubmitDirectorFork() {
    if (!episode || !explicitScene?.id || !onSubmitDirectorScene) return;
    const payload = director.getForkPayload(explicitScene.id);
    if (!payload) return;
    try {
      const submittedId = await onSubmitDirectorScene(
        episode.id,
        explicitScene.id,
        payload,
      );
      if (submittedId) {
        await director.markSubmitted(explicitScene.id, submittedId);
      }
    } catch (err) {
      console.error(err);
      window.alert(
        err instanceof Error ? err.message : "Kunne ikke aflevere scenen.",
      );
    }
  }

  async function handleUnlockDirectorConfirm(options: {
    archive: boolean;
    label?: string;
  }) {
    const sceneId = unlockDirectorSceneId;
    setUnlockDirectorSceneId(null);
    if (!sceneId) return;
    try {
      await director.unlockScene(sceneId, options);
    } catch (err) {
      console.error(err);
      window.alert(
        err instanceof Error ? err.message : "Kunne ikke låse scenen op.",
      );
    }
  }

  async function openDirectorArchiveBrowse(sceneId: Id) {
    try {
      await director.refreshArchives(sceneId);
      setBrowseDirectorArchiveSceneId(sceneId);
    } catch (err) {
      console.error(err);
      window.alert(
        err instanceof Error
          ? err.message
          : "Kunne ikke hente arkiverede versioner.",
      );
    }
  }

  const connectionBanner =
    cloudBacked && (syncStatus === "offline" || syncStatus === "error") ? (
      <ConnectionBanner
        kind={syncStatus === "error" ? "error" : "offline"}
        onExport={() => setShowPrint(true)}
      />
    ) : null;

  const chromeOverlays = (
    <>
      {showPrint ? (
        <PrintPreview
          document={document}
          episodeId={episode.id}
          view={currentView}
          showColumnRule={settings.printColumnRule}
          rowRuleMode={settings.rowRuleMode}
          defaultSceneHeaderBg={settings.defaultSceneHeaderBg}
          showHours={settings.durationShowHours}
          showFrames={settings.durationShowFrames}
          fps={settings.durationFps}
          showSetupNumbers={settings.showSetupNumbers}
          showCameraNotes={settings.showCameraNotes}
          showPosition={settings.showPosition}
          columnOrder={settings.scriptColumnOrder}
          activeNoteColumnId={activeNoteColumnId}
          activeSceneId={scene?.id ?? null}
          onClose={() => setShowPrint(false)}
          onExportRtf={(sceneIds) => void exportManual(sceneIds)}
          onExportDocx={(sceneIds) => void exportDocxManual(sceneIds)}
          onExportAudio={(sceneIds) => void exportAudioManual(sceneIds)}
          onExportAudioDocx={(sceneIds) => void exportAudioDocxManual(sceneIds)}
          lastSavedAt={lastSavedAt}
          exportError={exportError}
          showEpisodeNumbers={settings.showEpisodeNumbers}
        />
      ) : null}
      <HelpPanel
        open={helpOpen}
        sectionId={helpSection}
        onClose={() => setHelpOpen(false)}
        onSelectSection={setHelpSection}
      />
      <SettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        displayName={displayName}
        onRenameDisplayName={onRenameDisplayName}
        settings={settings}
        onSettingsChange={applySettingsPatch}
        onSaveUserPreferences={settingsActions.saveNow}
        onResetUserPreferences={settingsActions.resetToDefaults}
        useSeasons={Boolean(document.production.useSeasons)}
        onSetUseSeasons={onSetUseSeasons}
        sceneNumberingMode={
          editorScreen === "episode" ? numberingMode : undefined
        }
        onChangeSceneNumberingMode={
          editorScreen === "episode" ? onChangeSceneNumberingMode : undefined
        }
        episodeNumberingMode={
          document.production.episodeNumberingMode === "custom"
            ? "custom"
            : "sequential"
        }
        onChangeEpisodeNumberingMode={onChangeEpisodeNumberingMode}
        readOnly={settingsReadOnly}
        onShowResolvedComments={() => setShowResolvedComments(true)}
        resolvedCommentCount={listResolvedComments(document).length}
      />
      {numberingNotice ? (
        <SceneNumberingNoticeDialog
          kind={numberingNotice}
          onDismiss={() => setNumberingNotice(null)}
        />
      ) : null}
      {pendingRemoveNoteColumn && episode ? (
        <RemoveNoteColumnDialog
          columnTitle={pendingRemoveNoteColumn.title}
          report={reportNoteColumnContent(
            document,
            pendingRemoveNoteColumn.id,
            episode.id,
          )}
          showEpisodeNumbers={settings.showEpisodeNumbers}
          onCancel={() => setPendingRemoveNoteColumnId(null)}
          onConfirm={(scope) => {
            const columnId = pendingRemoveNoteColumn.id;
            setPendingRemoveNoteColumnId(null);
            onRemoveNoteColumn(columnId, scope);
            if (noteColumnId === columnId) {
              const nextVisible = noteColumns.filter((c) => c.id !== columnId);
              setNoteColumnId(nextVisible[0]?.id ?? null);
            }
          }}
        />
      ) : null}
      {copyToast ? (
        <div className="sm-copy-toast sm-no-print" role="status">
          {copyToast}
        </div>
      ) : null}
    </>
  );

  if (editorScreen === "dashboard") {
    return (
      <div className={`${editorRootClass} sm-editor-dashboard`}>
        {connectionBanner}
        <ProductionDashboard
          document={document}
          readOnly={baseReadOnly}
          settings={settings}
          displayName={displayName}
          onRenameDisplayName={onRenameDisplayName}
          onRenameProduction={onRenameProduction}
          onGoHome={onGoHome}
          onOpenHelp={openHelp}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenEpisode={openEpisodeFromDash}
          onOpenDirectorEpisode={
            apiBase ? openDirectorFromDash : undefined
          }
          onAddSeason={onAddSeason}
          onAddEpisode={onAddEpisode}
          onDeleteEpisode={onDeleteEpisode}
          onDuplicateEpisode={onDuplicateEpisode}
          onExportEpisodeBackup={onExportEpisodeBackup}
          onImportEpisodeBackup={onImportEpisodeBackup}
          onCopyEpisodeToProduction={onCopyEpisodeToProduction}
          onMoveEpisodeToProduction={onMoveEpisodeToProduction}
          onMoveEpisodeToSeason={onMoveEpisodeToSeason}
          otherProductions={otherProductions}
          onReorderEpisodes={onReorderEpisodes}
          onCopyEpisodeLink={
            shareLinkBase ? (episodeId) => copyTabLink(episodeId) : undefined
          }
          onRenameEpisode={onRenameEpisode}
          onChangeEpisodeNumber={
            onChangeEpisodeNumber ? changeEpisodeNumber : undefined
          }
          onRestoreTrashEpisode={onRestoreTrashEpisode}
          onPurgeTrashEpisode={onPurgeTrashEpisode}
          onRestoreTrashScene={onRestoreTrashScene}
          onPurgeTrashScene={onPurgeTrashScene}
          onEmptyTrash={onEmptyTrash}
          onRestoreArchiveScene={onRestoreArchiveScene}
          onPurgeArchiveScene={onPurgeArchiveScene}
          onPurgeArchiveEpisode={onPurgeArchiveEpisode}
          onEmptyArchive={onEmptyArchive}
          onInsertSubmittedIntoProgram={onInsertSubmittedIntoProgram}
          syncStatus={SYNC_LABEL[syncStatus] ?? syncStatus}
          licenseStatus={
            licenseStatus !== "ok"
              ? LICENSE_LABEL[licenseStatus] ?? licenseStatus
              : "ok"
          }
        />
        {chromeOverlays}
        {showResolvedComments ? (
          <ResolvedCommentsPanel
            document={document}
            readOnly={baseReadOnly}
            onClose={() => setShowResolvedComments(false)}
            onReopen={onReopenComment}
            onRemove={onRemoveComment}
            onOpenComment={({ sceneId, cueId, column }) => {
              setShowResolvedComments(false);
              const sc = document.scenes[sceneId];
              if (sc) openEpisodeFromDash(sc.episodeId, "script", sceneId);
              handleCueFocus(cueId, column);
            }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className={editorRootClass}>
      <div className="sm-main">
        <div className="sm-episode-workspace">
          <div className="sm-chrome-sticky sm-no-print">
            {connectionBanner}
            <div className="sm-episode-top">
              <div className="sm-episode-top-inner">
                <EpisodePageBar
                  title={
                    <EpisodeTitleLine
                      className="sm-folder-episode"
                      episode={episode}
                      showNumber={settings.showEpisodeNumbers}
                      readOnly={editLocked}
                      truncate
                      onRename={(title) => onRenameEpisode(episode.id, title)}
                      onNumberChange={(number) =>
                        changeEpisodeNumber(episode.id, number)
                      }
                    />
                  }
                  view={currentView}
                  scenes={scenes}
                  sceneId={scene?.id ?? null}
                  readOnly={editLocked}
                  onSelectScene={onSelectScene}
                  onChangeView={changeView}
                  tabOrder={settings.episodeTabOrder}
                  onAddScene={directorMode ? () => {} : onAddScene}
                  onAddDialogue={
                    directorMode ? handleAddDialogue : onAddDialogue
                  }
                  activeEditor={activeEditor}
                  rowSpanMode={SHOW_ROW_SPAN_UI && rowSpanMode}
                  onToggleRowSpanMode={() => {
                    setRowSpanMode((on) => !on);
                    setCellSelectMode(false);
                  }}
                  onOpenHelp={() => openHelp()}
                  onOpenSettings={() => setSettingsOpen(true)}
                  onPreparePrint={() => setShowPrint(true)}
                  onGoDashboard={goToDashboard}
                  onOpenFind={() => setFindOpen((v) => !v)}
                  findOpen={findOpen}
                  episodeZoom={settings.episodeZoom}
                  onEpisodeZoomChange={(zoom) =>
                    setSettings({ episodeZoom: clampEpisodeZoom(zoom) })
                  }
                  castMembers={document.production.castMembers ?? []}
                  onAddCastMember={onAddCastMember}
                  onUpdateCastMember={onUpdateCastMember}
                  onRemoveCastMember={onRemoveCastMember}
                />
                {vaultKind && vaultLabel ? (
                  <div className="sm-vault-browse-bar" role="status">
                    <p>
                      Du kigger i {vaultLabel.toLowerCase()}
                      {episode.originEpisodeTitle?.trim()
                        ? ` — ${episode.originEpisodeTitle.trim()}`
                        : ""}
                      {vaultKind === "submitted" &&
                      scene?.submittedByName?.trim()
                        ? ` — afleveret af ${scene.submittedByName.trim()}`
                        : ""}
                      . Indholdet kan ikke redigeres her.
                    </p>
                    <div className="sm-vault-browse-actions">
                      {!readOnly &&
                      vaultKind === "trash" &&
                      scene &&
                      onPurgeTrashScene ? (
                        <button
                          type="button"
                          className="sm-vault-danger"
                          onClick={() => {
                            if (
                              !window.confirm(
                                "Er du sikker på, at du vil slette scenen permanent fra papirkurven?\n\nDet kan ikke fortrydes.",
                              )
                            ) {
                              return;
                            }
                            const sceneId = scene.id;
                            onPurgeTrashScene(sceneId);
                            goToDashboard();
                          }}
                        >
                          Slet scene
                        </button>
                      ) : null}
                      {!readOnly &&
                      vaultKind === "archive" &&
                      scene &&
                      onPurgeArchiveScene ? (
                        <button
                          type="button"
                          className="sm-vault-danger"
                          onClick={() => {
                            if (
                              !window.confirm(
                                "Er du sikker på, at du vil slette denne arkiverede version permanent?\n\nDet kan ikke fortrydes.",
                              )
                            ) {
                              return;
                            }
                            const sceneId = scene.id;
                            onPurgeArchiveScene(sceneId);
                            goToDashboard();
                          }}
                        >
                          Slet version
                        </button>
                      ) : null}
                      {!readOnly &&
                      vaultKind === "trash" &&
                      onPurgeTrashEpisode ? (
                        <button
                          type="button"
                          className="sm-vault-danger"
                          onClick={() => {
                            const label =
                              episode.originEpisodeTitle?.trim() ||
                              episodeDisplayTitle;
                            if (
                              !window.confirm(
                                `Er du sikker på, at du vil slette «${label}» permanent fra papirkurven?\n\nDet kan ikke fortrydes.`,
                              )
                            ) {
                              return;
                            }
                            onPurgeTrashEpisode(episode.id);
                            goToDashboard();
                          }}
                        >
                          Slet episode
                        </button>
                      ) : null}
                      {!readOnly &&
                      vaultKind === "archive" &&
                      onPurgeArchiveEpisode ? (
                        <button
                          type="button"
                          className="sm-vault-danger"
                          onClick={() => {
                            const label =
                              episode.originEpisodeTitle?.trim() ||
                              episodeDisplayTitle;
                            if (
                              !window.confirm(
                                `Er du sikker på, at du vil slette alle arkiverede versioner for «${label}» permanent?\n\nDet kan ikke fortrydes.`,
                              )
                            ) {
                              return;
                            }
                            onPurgeArchiveEpisode(episode.id);
                            goToDashboard();
                          }}
                        >
                          Slet alle versioner
                        </button>
                      ) : null}
                      {vaultKind === "submitted" &&
                      scene &&
                      onInsertSubmittedIntoProgram ? (
                        <button
                          type="button"
                          className="sm-dialog-primary"
                          onClick={() =>
                            setPendingInsertSubmittedSceneId(scene.id)
                          }
                        >
                          Indsæt i programmanus
                        </button>
                      ) : null}
                      <button type="button" onClick={goToDashboard}>
                        Tilbage til forsiden
                      </button>
                    </div>
                  </div>
                ) : null}
                {directorMode && !vaultKind ? (
                  <div className="sm-director-bar" role="status">
                    <p>
                      <span className="sm-director-badge">
                        Tilrettelæggermanus
                      </span>
                      {displayName.trim() ? (
                        <span className="sm-director-user">
                          {" "}
                          · {displayName.trim()}
                        </span>
                      ) : null}
                      {directorSceneFocus && activeSceneSubmitLocked ? (
                        <span className="sm-director-submitted-badge">
                          {" "}
                          · Afleveret
                          {activeSubmitState?.submittedConsumed
                            ? " (indsat i programmanus)"
                            : ""}
                        </span>
                      ) : null}
                      {directorSceneFocus && activeSceneEditsSubmitted ? (
                        <span className="sm-director-submitted-badge">
                          {" "}
                          · Redigerer afleveret version
                        </span>
                      ) : null}
                      {director.loading ? " — henter…" : null}
                      {director.error ? (
                        <span className="sm-dialog-hint"> {director.error}</span>
                      ) : null}
                    </p>
                    <div className="sm-director-bar-actions">
                      {directorSceneFocus &&
                      explicitScene &&
                      !activeSceneHasFork ? (
                        <button
                          type="button"
                          className="sm-dialog-primary"
                          onClick={() => void handleCreateDirectorFork()}
                        >
                          Opret min version
                        </button>
                      ) : null}
                      {directorSceneFocus &&
                      explicitScene &&
                      activeSceneHasFork &&
                      activeSceneSubmitLocked ? (
                        <button
                          type="button"
                          className="sm-dialog-primary"
                          onClick={() =>
                            setUnlockDirectorSceneId(explicitScene.id)
                          }
                        >
                          Lås op
                        </button>
                      ) : null}
                      {directorSceneFocus &&
                      explicitScene &&
                      activeSceneHasFork &&
                      !activeSceneSubmitLocked ? (
                        <>
                          {onSubmitDirectorScene &&
                          !activeSceneEditsSubmitted ? (
                            <button
                              type="button"
                              className="sm-dialog-primary"
                              onClick={() => void handleSubmitDirectorFork()}
                            >
                              Aflever
                            </button>
                          ) : null}
                          {activeSceneEditsSubmitted ? (
                            <span className="sm-dialog-hint">
                              Ændringer gemmes under Afleverede scener
                            </span>
                          ) : null}
                          <button
                            type="button"
                            onClick={() =>
                              setDirectorArchiveSceneId(explicitScene.id)
                            }
                          >
                            Arkivér min version
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              void openDirectorArchiveBrowse(explicitScene.id)
                            }
                          >
                            Arkiverede versioner…
                          </button>
                        </>
                      ) : null}
                      <button type="button" onClick={goToDashboard}>
                        Tilbage til forsiden
                      </button>
                    </div>
                  </div>
                ) : null}
                {findOpen ? (
                  <FindPanel
                    open={findOpen}
                    onClose={() => setFindOpen(false)}
                    document={viewDoc}
                    episodeId={episode.id}
                    sceneId={scene?.id ?? null}
                    noteColumns={noteColumns}
                    onJump={handleFindJump}
                  />
                ) : null}
              </div>
            </div>
            {splitPicking ? (
              <div className="sm-split-pick-bar" role="status">
                <p>
                  Klik på linjen mellem de to rækker, der skal skilles.
                  Esc annullerer.
                </p>
                <button
                  type="button"
                  onClick={() => setSplitPickSceneId(null)}
                >
                  Annuller
                </button>
              </div>
            ) : null}
          </div>
          <PaperFit
            widthMm={episodeFolderLandscape(currentView) ? 297 : 210}
            userZoom={settings.episodeZoom}
          >
          <EditorErrorBoundary label="manus">
          <div
            className={`sm-folder${
              episodeFolderLandscape(currentView) ? " is-landscape" : ""
            }`}
          >
            <EpisodeFolderTabs
              view={currentView}
              onChangeView={changeView}
              tabOrder={settings.episodeTabOrder}
              onReorderTabs={(order) =>
                setSettings({ episodeTabOrder: order })
              }
              onCopyTabLink={
                shareLinkBase && episode
                  ? (tab) => copyTabLink(episode.id, tab)
                  : undefined
              }
            />
            <div className="sm-folder-page">
        {currentView === "timing" ? (
          <TimingView
            scenes={scenes}
            readOnly={effectiveReadOnly}
            productionTitle={document.production.title}
            seasonTitle={seasonTitle}
            episodeTitle={episodeDisplayTitle}
            defaultSceneHeaderBg={settings.defaultSceneHeaderBg}
            sceneRowClass={directorSceneRowClass}
            onDurationChange={(sceneId, durationSeconds) =>
              handleSceneMetaChange(sceneId, { durationSeconds })
            }
            onMetaChange={handleSceneMetaChange}
            onMoveScene={requestMoveScene}
            onSceneHeaderContextMenu={(sceneId, e) =>
              openSceneColorMenu(sceneId, e)
            }
            onOpenScene={(sceneId) => {
              onSelectScene(sceneId);
              changeView("script");
            }}
            showHours={settings.durationShowHours}
            showFrames={settings.durationShowFrames}
            fps={settings.durationFps}
          />
        ) : null}

        {currentView === "outline" ? (
          <OutlineView
            scenes={scenes}
            readOnly={effectiveReadOnly}
            productionTitle={document.production.title}
            seasonTitle={seasonTitle}
            episodeTitle={episodeDisplayTitle}
            defaultSceneHeaderBg={settings.defaultSceneHeaderBg}
            sceneRowClass={directorSceneRowClass}
            onMetaChange={handleSceneMetaChange}
            onMoveScene={requestMoveScene}
            onSceneHeaderContextMenu={(sceneId, e) =>
              openSceneColorMenu(sceneId, e)
            }
            showHours={settings.durationShowHours}
            showFrames={settings.durationShowFrames}
            fps={settings.durationFps}
          />
        ) : null}

        {currentView === "crew" ? (
          <CrewView
            scenes={scenes}
            episodeCrewRoles={mergeSeasonCrewIntoEpisodeRoles(
              episode.crewRoles,
              getEpisodePeerCrewRoles(document, episode.id),
            )}
            readOnly={effectiveReadOnly || directorMode}
            productionTitle={document.production.title}
            seasonTitle={seasonTitle}
            episodeTitle={episodeDisplayTitle}
            defaultSceneHeaderBg={settings.defaultSceneHeaderBg}
            nameSuggestionsByJob={listEpisodeCrewNamesByJob(
              document,
              episode.id,
            )}
            jobTitles={getCrewJobTitleOptions(document.production)}
            onChangeCrewRoles={onChangeCrewRoles}
            onChangeEpisodeCrewRoles={onChangeEpisodeCrewRoles}
            onRememberJobTitle={onRememberJobTitle}
            onRemoveJobTitle={onRemoveJobTitle}
            seasonCredits={
              onToggleSeasonCrewPerson &&
              onDeactivateSeasonCrewPerson &&
              onRemoveSeasonCrewPerson
                ? {
                    hasPerson: (jobTitle, person) =>
                      episodePeerHasCrewPerson(
                        document,
                        episode.id,
                        jobTitle,
                        person,
                      ),
                    onActivatePerson: (jobTitle, person) =>
                      onToggleSeasonCrewPerson(
                        episode.id,
                        jobTitle,
                        person,
                        true,
                      ),
                    onDeactivatePerson: (jobTitle, person, mode) =>
                      onDeactivateSeasonCrewPerson(
                        episode.id,
                        jobTitle,
                        person,
                        mode,
                      ),
                    onRemoveSeasonPerson: (jobTitle, person) =>
                      onRemoveSeasonCrewPerson(
                        episode.id,
                        jobTitle,
                        person,
                      ),
                  }
                : undefined
            }
            seasonCrewRoles={getEpisodePeerCrewRoles(document, episode.id)}
            onPrintAggregate={() => setShowPrint(true)}
          />
        ) : null}

        {currentView === "programtext" ? (
          <ProgramTextView
            texts={getEpisodeProgramTexts(episode)}
            readOnly={effectiveReadOnly || directorMode}
            productionTitle={document.production.title}
            seasonTitle={seasonTitle}
            episodeTitle={episodeDisplayTitle}
            onChange={onChangeProgramTexts}
          />
        ) : null}

            {currentView === "sources" && scene ? (
          <div
            className={`sm-script-sheet${activeSceneSheetClass ? ` ${activeSceneSheetClass}` : ""}`}
            key={`sources-${scene.id}`}
          >
            <SourcesView
              scene={scene}
              cues={cues}
              readOnly={effectiveReadOnly}
              productionTitle={document.production.title}
              seasonTitle={seasonTitle}
              episodeTitle={episodeDisplayTitle}
              defaultSceneHeaderBg={settings.defaultSceneHeaderBg}
              showColumnRule={settings.printColumnRule}
              rowRuleMode={settings.rowRuleMode}
              rowRuleHitInGutter={settings.rowRuleHitInGutter}
              columnOrder={settings.scriptColumnOrder}
              noteColumns={noteColumns}
              activeNoteColumnId={activeNoteColumnId}
              onSelectNoteColumn={setNoteColumnId}
              onRenameNoteColumn={(columnId, title) => {
                onChangeNoteColumns(
                  productionNoteColumns.map((c) =>
                    c.id === columnId ? { ...c, title } : c,
                  ),
                );
              }}
              onAddNoteColumn={() => {
                const id = createId();
                onChangeNoteColumns([
                  ...productionNoteColumns,
                  {
                    id,
                    title: "Ny kolonne",
                    sortOrder: productionNoteColumns.length,
                  },
                ]);
                setNoteColumnId(id);
              }}
              onRemoveNoteColumn={(columnId) => {
                if (noteColumns.length <= 1) return;
                setPendingRemoveNoteColumnId(columnId);
              }}
              onCueNoteChange={handleCueNoteChange}
              onSceneNoteChange={
                onSceneNoteChange || directorMode
                  ? (columnId, text) =>
                      handleSceneNoteChange(scene.id, columnId, text)
                  : undefined
              }
              onCueChange={handleCueChange}
              onNewPair={handleNewPair}
              newRowOnDoubleReturn={settings.newRowOnDoubleReturn}
              onCueFocus={handleCueFocus}
              onCueBlur={onCueBlur}
              onActiveEditorChange={setLiveActiveEditor}
              contentResetKey={contentResetKey}
              onSceneMetaChange={(patch) =>
                handleSceneMetaChange(scene.id, patch)
              }
              showHours={settings.durationShowHours}
              showFrames={settings.durationShowFrames}
              fps={settings.durationFps}
              clickSelectsCell={settings.clickSelectsCell}
              onSceneHeaderContextMenu={(e) =>
                openSceneColorMenu(scene.id, e)
              }
              onCopyNoteColumnLink={
                shareLinkBase
                  ? (columnId) => copyNoteColumnLink(scene.id, columnId)
                  : undefined
              }
              focusNoteCueId={findFocusNote?.cueId ?? null}
              focusNoteSelectQuery={findFocusNote?.query}
              focusNoteSelectOccurrence={findFocusNote?.occurrence}
              focusNoteNonce={findFocusNote?.nonce}
              onFocusNoteCueHandled={() => setFindFocusNote(null)}
            />
          </div>
        ) : null}

        {currentView === "status" ? (
          <StatusView
            scenes={scenes}
            readOnly={effectiveReadOnly}
            productionTitle={document.production.title}
            seasonTitle={seasonTitle}
            episodeTitle={episodeDisplayTitle}
            defaultSceneHeaderBg={settings.defaultSceneHeaderBg}
            sceneRowClass={directorSceneRowClass}
            statusSteps={document.production.statusSteps ?? []}
            episodeStatusSteps={episode.statusSteps ?? []}
            episodeStatusCompletedIds={episode.statusCompletedIds ?? []}
            onToggleStep={handleToggleStatusStep}
            onToggleEpisodeStep={onToggleEpisodeStatusStep}
            onChangeProductionSteps={onChangeStatusSteps}
            onChangeSceneSteps={handleChangeSceneStatusSteps}
            onChangeEpisodeSteps={onChangeEpisodeStatusSteps}
            showHours={settings.durationShowHours}
            showFrames={settings.durationShowFrames}
            fps={settings.durationFps}
          />
        ) : null}

            {currentView === "script" && scene ? (
          <div
            className={`sm-script-sheet${activeSceneSheetClass ? ` ${activeSceneSheetClass}` : ""}`}
            key={`script-${scene.id}`}
          >
            <div className="sm-paper sm-paper-a4">
              {splitPicking ? null : (
              <div className="sm-paper-tools sm-no-print">
                <button
                  type="button"
                  className={cellSelectMode ? "is-active" : undefined}
                  title={
                    cellSelectMode
                      ? "Slå fra for at markere tekst i cellen"
                      : "Vælg flere celler — træk med musen for at kopiere eller slette"
                  }
                  aria-pressed={cellSelectMode}
                  disabled={effectiveReadOnly}
                  onClick={() => {
                    setCellSelectMode((on) => !on);
                    setRowSpanMode(false);
                  }}
                >
                  Markér celler
                </button>
                <ViewMenu
                  settings={settings}
                  onSettingsChange={applySettingsPatch}
                  align="end"
                />
              </div>
              )}
              <DocumentHead
                productionTitle={document.production.title}
                seasonTitle={seasonTitle}
                episodeTitle={episodeDisplayTitle}
                view="script"
              />
              <div
                className="sm-script-scene-head"
                style={{
                  backgroundColor: sceneHeaderBg(
                    scene.headerBackground,
                    settings.defaultSceneHeaderBg,
                  ),
                }}
                onContextMenu={(e) => {
                  if (splitPicking) {
                    e.preventDefault();
                    return;
                  }
                  openSceneColorMenu(scene.id, e);
                }}
                title={
                  effectiveReadOnly
                    ? undefined
                    : "Højreklik for baggrundsfarve, dupliker, flyt, opdel eller slet"
                }
              >
                <div className="sm-script-scene-head-row">
                  <div className="sm-script-scene-head-main">
                    <SceneHeadingLine
                      sceneNumber={scene.number}
                      heading={scene.heading}
                      headingKind={scene.headingKind}
                      headingLocation={scene.headingLocation}
                      headingTime={scene.headingTime}
                      headingNote={scene.headingNote}
                      readOnly={editLocked}
                      onChange={(patch) =>
                        handleSceneMetaChange(scene.id, patch)
                      }
                      onNumberChange={(number) =>
                        handleSceneMetaChange(scene.id, { number })
                      }
                    />
                    <InlineEditable
                      as="strong"
                      fill
                      className="sm-script-scene-title"
                      value={scene.title}
                      onChange={(title) =>
                        handleSceneMetaChange(scene.id, { title })
                      }
                      readOnly={editLocked}
                      placeholder="Unavngiven scene"
                    />
                  </div>
                  <DurationField
                    className="sm-script-scene-duration"
                    valueSeconds={scene.durationSeconds}
                    onChange={(durationSeconds) =>
                      handleSceneMetaChange(scene.id, { durationSeconds })
                    }
                    readOnly={editLocked}
                    showHours={settings.durationShowHours}
                    showFrames={settings.durationShowFrames}
                    fps={settings.durationFps}
                    ariaLabel={`Varighed for scene ${scene.number}`}
                  />
                </div>
              </div>
              <DualColumnScript
                cues={cues}
                userId={userId}
                readOnly={editLocked}
                splitPickMode={splitPicking}
                onPickSplitAt={
                  onSplitScene
                    ? (atCueId) => {
                        if (!splitPickSceneId) return;
                        onSplitScene(splitPickSceneId, atCueId);
                        setSplitPickSceneId(null);
                      }
                    : undefined
                }
                confirmDeleteEmptyPair={settings.confirmDeleteEmptyPair}
                newRowOnDoubleReturn={settings.newRowOnDoubleReturn}
                showColumnRule={settings.printColumnRule}
                rowRuleMode={settings.rowRuleMode}
                rowRuleHitInGutter={settings.rowRuleHitInGutter}
                castMembers={document.production.castMembers ?? []}
                comments={Object.values(viewDoc.comments ?? {})}
                showComments={settings.showComments && !splitPicking}
                sceneNumber={scene.number}
                showSetupNumbers={settings.showSetupNumbers}
                showCameraNotes={settings.showCameraNotes}
                showPosition={settings.showPosition}
                onCameraMetaChange={handleCameraMetaChange}
                columnOrder={settings.scriptColumnOrder}
                cellSelectMode={cellSelectMode && !splitPicking}
                clickSelectsCell={settings.clickSelectsCell && !splitPicking}
                rowSpanMode={SHOW_ROW_SPAN_UI && rowSpanMode && !splitPicking}
                noteColumns={noteColumns}
                focusCell={findFocusCell ?? focusCell}
                onFocusCellHandled={(cell) => {
                  setFindFocusCell((cur) =>
                    cur?.cueId === cell.cueId && cur.column === cell.column
                      ? null
                      : cur,
                  );
                  onFocusCellHandled?.(cell);
                }}
                contentResetKey={contentResetKey}
                onFocus={handleCueFocus}
                onBlur={onCueBlur}
                onActiveEditorChange={setLiveActiveEditor}
                onChange={handleCueChange}
                onMoveCueBlock={handleMoveCueBlock}
                onNewPair={handleNewPair}
                onDeletePair={handleDeletePair}
                onDeletePairs={handleDeletePairs}
                onSetVisualSpan={handleSetVisualSpan}
                onSetAudioSpan={handleSetAudioSpan}
                onTogglePrintBorderBelow={handleTogglePrintBorderBelow}
                onAddComment={handleAddComment}
                onResolveComment={handleResolveComment}
                onRemoveComment={handleRemoveComment}
                onSetCharacter={handleSetCharacter}
              />
            </div>
          </div>
        ) : null}
            </div>
          </div>
          </EditorErrorBoundary>
          </PaperFit>
        </div>
      </div>

      {pendingSceneMove ? (
        <SceneMoveDialog
          mode={numberingMode}
          onCancel={() => setPendingSceneMove(null)}
          onKeepNumbers={() => confirmSceneMove(false)}
          onRenumber={() => confirmSceneMove(true)}
        />
      ) : null}

      {browseArchiveSceneId && onRestoreArchiveScene ? (
        <SceneArchiveVersionsDialog
          document={document}
          liveSceneId={browseArchiveSceneId}
          userId={userId}
          noteColumns={noteColumns}
          defaultSceneHeaderBg={settings.defaultSceneHeaderBg}
          showColumnRule={settings.printColumnRule}
          rowRuleMode={settings.rowRuleMode}
          rowRuleHitInGutter={settings.rowRuleHitInGutter}
          columnOrder={settings.scriptColumnOrder}
          showSetupNumbers={settings.showSetupNumbers}
          showCameraNotes={settings.showCameraNotes}
          showPosition={settings.showPosition}
          showHours={settings.durationShowHours}
          showFrames={settings.durationShowFrames}
          fps={settings.durationFps}
          onCancel={() => setBrowseArchiveSceneId(null)}
          onRestore={(archivedSceneId, options) => {
            onRestoreArchiveScene(archivedSceneId, options);
          }}
        />
      ) : null}

      {directorArchiveSceneId ? (
        <ArchiveSceneDialog
          sceneLabel={(() => {
            const target = viewDoc.scenes[directorArchiveSceneId];
            if (!target) return "scenen";
            const name = target.title.trim() || "Unavngiven scene";
            return `scene ${target.number} «${name}»`;
          })()}
          existing={(
            director.archivesByScene[directorArchiveSceneId] ?? []
          ).map((row) => ({
            ...row.payload.scene,
            id: row.id,
            archivedAt: row.archivedAt,
            archiveLabel: row.archiveLabel ?? undefined,
          }))}
          onCancel={() => setDirectorArchiveSceneId(null)}
          onConfirm={(options) => {
            const sceneId = directorArchiveSceneId;
            setDirectorArchiveSceneId(null);
            void (async () => {
              try {
                await director.archiveFork(sceneId, options);
              } catch (err) {
                console.error(err);
                window.alert(
                  err instanceof Error
                    ? err.message
                    : "Kunne ikke arkivere din version.",
                );
              }
            })();
          }}
        />
      ) : null}

      {browseDirectorArchiveSceneId ? (
        <SceneArchiveVersionsDialog
          document={buildDirectorArchiveBrowseDocument(
            document,
            browseDirectorArchiveSceneId,
            director.archivesByScene[browseDirectorArchiveSceneId] ?? [],
          )}
          liveSceneId={browseDirectorArchiveSceneId}
          userId={userId}
          noteColumns={noteColumns}
          defaultSceneHeaderBg={settings.defaultSceneHeaderBg}
          showColumnRule={settings.printColumnRule}
          rowRuleMode={settings.rowRuleMode}
          rowRuleHitInGutter={settings.rowRuleHitInGutter}
          columnOrder={settings.scriptColumnOrder}
          showSetupNumbers={settings.showSetupNumbers}
          showCameraNotes={settings.showCameraNotes}
          showPosition={settings.showPosition}
          showHours={settings.durationShowHours}
          showFrames={settings.durationShowFrames}
          fps={settings.durationFps}
          onCancel={() => setBrowseDirectorArchiveSceneId(null)}
          onRestore={(archivedSceneId, _options) => {
            const rows =
              director.archivesByScene[browseDirectorArchiveSceneId] ?? [];
            const row = rows.find((r) => r.id === archivedSceneId);
            if (row) {
              director.replaceForkPayload(
                browseDirectorArchiveSceneId,
                row.payload,
              );
            }
          }}
        />
      ) : null}

      {unlockDirectorSceneId ? (
        <UnlockDirectorDialog
          sceneLabel={(() => {
            const target = viewDoc.scenes[unlockDirectorSceneId];
            if (!target) return "scenen";
            const name = target.title.trim() || "Unavngiven scene";
            return `scene ${target.number} «${name}»`;
          })()}
          consumedWarning={
            director.getSubmitState(unlockDirectorSceneId)?.submittedConsumed
              ? "Den tidligere afleverede version af scenen er allerede blevet tilføjet til programmanus. Efter redigering skal du aflevere scenen på ny."
              : null
          }
          onCancel={() => setUnlockDirectorSceneId(null)}
          onConfirm={(options) => void handleUnlockDirectorConfirm(options)}
        />
      ) : null}

      {pendingInsertSubmittedSceneId && onInsertSubmittedIntoProgram ? (
        <RestoreArchiveDialog
          title="Indsæt i programmanus"
          lead={(() => {
            const target = document.scenes[pendingInsertSubmittedSceneId];
            if (!target) return "Vælg, hvad der skal ske med den nuværende scene.";
            const name = target.title.trim() || "Unavngiven scene";
            return `Du er ved at indsætte scene ${target.number} «${name}» i programmanus. Vælg, hvad der skal ske med den nuværende scene.`;
          })()}
          legend="Hvordan vil du indsætte?"
          confirmLabel="Indsæt"
          keepHint="den afleverede version indsættes som en ekstra scene"
          sceneLabel={(() => {
            const target = document.scenes[pendingInsertSubmittedSceneId];
            if (!target) return "scenen";
            const name = target.title.trim() || "Unavngiven scene";
            return `scene ${target.number} «${name}»`;
          })()}
          onCancel={() => setPendingInsertSubmittedSceneId(null)}
          onConfirm={(mode) => {
            const sceneId = pendingInsertSubmittedSceneId;
            setPendingInsertSubmittedSceneId(null);
            onInsertSubmittedIntoProgram(sceneId, { mode });
            goToDashboard();
          }}
        />
      ) : null}

      {archiveSceneId && onArchiveScene ? (
        <ArchiveSceneDialog
          sceneLabel={(() => {
            const target = document.scenes[archiveSceneId];
            if (!target) return "scenen";
            const name = target.title.trim() || "Unavngiven scene";
            return `scene ${target.number} «${name}»`;
          })()}
          existing={listArchivedVersions(document, archiveSceneId)}
          onCancel={() => setArchiveSceneId(null)}
          onConfirm={(options) => {
            const sceneId = archiveSceneId;
            setArchiveSceneId(null);
            onArchiveScene(sceneId, options);
          }}
        />
      ) : null}

      {transferSceneId &&
      (onCopySceneToEpisode || onCopySceneToProduction) &&
      (onMoveSceneToEpisode || onMoveSceneToProduction) ? (
        <SceneTransferDialog
          sceneLabel={(() => {
            const target = document.scenes[transferSceneId];
            if (!target) return "Scenen";
            const name = target.title.trim() || "Unavngiven scene";
            return `Scene ${target.number} (${name})`;
          })()}
          currentProductionTitle={document.production.title}
          episodesInCurrent={episodes
            .filter((ep) => ep.id !== episode.id)
            .map((ep) => ({ id: ep.id, label: episodeChoiceLabel(ep) }))}
          otherProductions={otherProductions ?? []}
          canMove={scenes.length > 1}
          onLoadProductionEpisodes={onLoadProductionEpisodes}
          onCancel={() => setTransferSceneId(null)}
          onCopy={(dest) => {
            const sceneId = transferSceneId;
            setTransferSceneId(null);
            if (dest.productionId) {
              onCopySceneToProduction?.(
                sceneId,
                dest.productionId,
                dest.episodeId,
              );
            } else {
              onCopySceneToEpisode?.(sceneId, dest.episodeId);
            }
          }}
          onMove={(dest) => {
            const sceneId = transferSceneId;
            setTransferSceneId(null);
            if (dest.productionId) {
              onMoveSceneToProduction?.(
                sceneId,
                dest.productionId,
                dest.episodeId,
              );
            } else {
              onMoveSceneToEpisode?.(sceneId, dest.episodeId);
            }
          }}
        />
      ) : null}

      {pendingMerge && onMergeScenes
        ? (() => {
            const keep = document.scenes[pendingMerge.keepSceneId];
            const other = document.scenes[pendingMerge.otherSceneId];
            if (!keep || !other) return null;
            const keepIndex = scenes.findIndex((s) => s.id === keep.id);
            const otherIndex = scenes.findIndex((s) => s.id === other.id);
            return (
              <SceneMergeDialog
                keep={keep}
                other={other}
                otherIsFirst={otherIndex >= 0 && otherIndex < keepIndex}
                customNumbers={numberingMode === "custom"}
                onCancel={() => setPendingMerge(null)}
                onConfirm={(identity) => {
                  onMergeScenes(keep.id, other.id, identity);
                  setPendingMerge(null);
                }}
              />
            );
          })()
        : null}

      {colorMenu && colorMenuScene ? (
        <SceneHeaderColorMenu
          x={colorMenu.x}
          y={colorMenu.y}
          currentColor={sceneHeaderBg(
            colorMenuScene.headerBackground,
            settings.defaultSceneHeaderBg,
          )}
          defaultColor={settings.defaultSceneHeaderBg}
          hasCustomColor={Boolean(colorMenuScene.headerBackground?.trim())}
          onPick={(color) =>
            handleSceneMetaChange(colorMenu.sceneId, {
              headerBackground: color,
            })
          }
          onReset={() =>
            handleSceneMetaChange(colorMenu.sceneId, {
              headerBackground: "",
            })
          }
          onClose={closeSceneColorMenu}
          canDeleteScene={scenes.length > 1 || episodes.length > 1}
          onDuplicateScene={
            onDuplicateScene && !effectiveReadOnly && !directorMode
              ? () => onDuplicateScene(colorMenu.sceneId)
              : undefined
          }
          onArchiveScene={
            onArchiveScene && !effectiveReadOnly && !directorMode
              ? () => setArchiveSceneId(colorMenu.sceneId)
              : undefined
          }
          onBrowseArchiveVersions={
            onRestoreArchiveScene &&
            !directorMode &&
            listArchivedVersions(document, colorMenu.sceneId).length > 0
              ? () => setBrowseArchiveSceneId(colorMenu.sceneId)
              : undefined
          }
          onTransferScene={
            (onCopySceneToEpisode || onCopySceneToProduction) &&
            (onMoveSceneToEpisode || onMoveSceneToProduction) &&
            !effectiveReadOnly &&
            !directorMode
              ? () => setTransferSceneId(colorMenu.sceneId)
              : undefined
          }
          canTransferScene={
            episodes.some((ep) => ep.id !== episode.id) ||
            Boolean(otherProductions && otherProductions.length > 0)
          }
          onSplitScene={
            onSplitScene &&
            !effectiveReadOnly &&
            !directorMode &&
            !splitPicking &&
            currentView === "script"
              ? () => setSplitPickSceneId(colorMenu.sceneId)
              : undefined
          }
          canSplitScene={
            getSceneCues(viewDoc, colorMenu.sceneId).length >= 2
          }
          onCopySceneLink={
            shareLinkBase
              ? () => copySceneLink(colorMenu.sceneId)
              : undefined
          }
          onMergePrev={
            onMergeScenes && !effectiveReadOnly && !directorMode
              ? () => {
                  const index = scenes.findIndex(
                    (s) => s.id === colorMenu.sceneId,
                  );
                  const prev = index > 0 ? scenes[index - 1] : null;
                  if (prev) {
                    setPendingMerge({
                      keepSceneId: colorMenu.sceneId,
                      otherSceneId: prev.id,
                    });
                  }
                }
              : undefined
          }
          canMergePrev={
            scenes.findIndex((s) => s.id === colorMenu.sceneId) > 0
          }
          onMergeNext={
            onMergeScenes && !effectiveReadOnly && !directorMode
              ? () => {
                  const index = scenes.findIndex(
                    (s) => s.id === colorMenu.sceneId,
                  );
                  const next = scenes[index + 1];
                  if (next) {
                    setPendingMerge({
                      keepSceneId: colorMenu.sceneId,
                      otherSceneId: next.id,
                    });
                  }
                }
              : undefined
          }
          canMergeNext={
            scenes.findIndex((s) => s.id === colorMenu.sceneId) >= 0 &&
            scenes.findIndex((s) => s.id === colorMenu.sceneId) <
              scenes.length - 1
          }
          onDeleteScene={
            onDeleteScene && !effectiveReadOnly && !directorMode
              ? () => {
                  const target = colorMenuScene;
                  const warnings = listSceneLinkedWarnings(
                    document,
                    target.id,
                  );
                  const ok = window.confirm(
                    buildSceneDeleteConfirmMessage({
                      sceneNumber: target.number,
                      sceneTitle: target.title,
                      linkedWarnings: warnings,
                      retentionDays: VAULT_RETENTION_DAYS,
                    }),
                  );
                  if (!ok) return;
                  onDeleteScene(target.id);
                }
              : undefined
          }
        />
      ) : null}

      {showResolvedComments ? (
        <ResolvedCommentsPanel
          document={document}
          readOnly={effectiveReadOnly}
          onClose={() => setShowResolvedComments(false)}
          onReopen={onReopenComment}
          onRemove={onRemoveComment}
          onOpenComment={({ sceneId, cueId, column }) => {
            setShowResolvedComments(false);
            onChangeView("script");
            onSelectScene(sceneId);
            handleCueFocus(cueId, column);
          }}
        />
      ) : null}

      {chromeOverlays}
    </div>
  );
}
